import { getDateWithoutTime } from '@/app/components/search/utils/dates/getDateWithoutTime.ts';
import { format } from 'date-fns';
import {
  getDateFnsLocaleByCode,
  localesWithSSRMismatchBetweenServerAndClient,
} from '@/app/components/search/utils/dates/dateFnsUtils.ts';
import { getLanguageFromLocale } from '../misc';
import {
  formatDateShortDayMonthException,
  formatDateShortException,
  formatDateShortMonthYearException,
  isExceptionLocale,
} from './dateExceptions';

enum Language {
  DE = 'DE',
  SK = 'SK',
}

const isGermany = (locale: string): boolean => getLanguageFromLocale(locale).toUpperCase() === Language.DE;

const adaptDate = (locale: string, date: string): string => (isGermany(locale) ? date.replace('Sept.', 'Sep.') : date);

// expected format: "Sep"
export const formatDateShort = (locale: string, date: string): string => {
  const adaptedDate = adaptDate(
    locale,
    getDateWithoutTime(date).toLocaleString(locale, {
      day: 'numeric',
      weekday: 'short',
      month: 'short',
    })
  );
  return isExceptionLocale(locale) ? formatDateShortException(locale, date) : adaptedDate;
};

// expected format: "Mon"
export const formatDateShortWeekday = (locale: string, date: string): string =>
  adaptDate(
    locale,
    getDateWithoutTime(date).toLocaleString(locale, {
      weekday: 'short',
    })
  );

// expected format: "Sep 12"
export const formatDateShortDayMonth = (locale: string, date: string): string => {
  const adaptedDate = adaptDate(
    locale,
    getDateWithoutTime(date).toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
    })
  );
  return isExceptionLocale(locale) ? formatDateShortDayMonthException(locale, date) : adaptedDate;
};

// expected format: "Sep 12, 2023"
export const formatDateShortDayMonthYear = (locale: string, date: string): string => {
  const adaptedDate = adaptDate(
    locale,
    getDateWithoutTime(date).toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  );
  return isExceptionLocale(locale) ? formatDateShortMonthYearException(locale, date) : adaptedDate;
};

// expected format: "Mon, Sep 12"
export const formatDateShortDayMonth2 = (locale: string, date: string): string => {
  const adaptedDate = adaptDate(
    locale,
    getDateWithoutTime(date).toLocaleString(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
  );

  return isExceptionLocale(locale) ? formatDateShortDayMonthException(locale, date) : adaptedDate;
};

// expected format: "Mon, 12 Sep"
export const formatDateShortDayMonthWithDateFn = (locale: string, date: string) => {
  const localeObj = getDateFnsLocaleByCode(locale);
  return adaptDate(locale, format(new Date(date), 'EEE, d MMM', { locale: localeObj }));
};

// expected format: "Mon, 12 Sep"
export const formatDateShortDayMonthIsomorphic = (locale: string, date: string): string => {
  const shouldUseDateFn = localesWithSSRMismatchBetweenServerAndClient(locale);

  if (shouldUseDateFn) {
    return formatDateShortDayMonthWithDateFn(locale, date);
  }

  return formatDateShortDayMonth2(locale, date);
};

// expected format: "Sep 2023"
export const formatDateShortMonthYear = (locale: string, date: string): string => {
  const adaptedDate = adaptDate(
    locale,
    getDateWithoutTime(date).toLocaleString(locale, {
      month: 'short',
      year: 'numeric',
    })
  );
  return isExceptionLocale(locale) ? formatDateShortMonthYearException(locale, date) : adaptedDate;
};

// expected format: "09:05" or "09:05 AM"
export const formatTimeTwoDigit = (value: string, locale: string): string =>
  new Date(value).toLocaleString(locale, { hour: '2-digit', minute: '2-digit', hour12: locale === 'en-US' });
